/** InsCoder 2026-09-14 B + Deduplicate in-flight cart reads across fetch and XMLHttpRequest **/
(() => {
  const installKey = Symbol.for('wnp.cartRequestCache.installed');
  if (window[installKey]) return;
  window[installKey] = true;

  const nativeFetch = window.fetch.bind(window);
  const NativeXMLHttpRequest = window.XMLHttpRequest;
  const cache = {
    generation: 0,
    inFlight: null
  };

  function toUrl(input) {
    try {
      const value = input instanceof Request ? input.url : input;
      return new URL(value, window.location.origin);
    } catch (error) {
      return null;
    }
  }

  function requestMethod(input, options = {}) {
    const inputMethod = input instanceof Request ? input.method : 'GET';
    return String(options.method || inputMethod || 'GET').toUpperCase();
  }

  function isCartRead(method, url) {
    return Boolean(
      url &&
      url.origin === window.location.origin &&
      method === 'GET' &&
      /(?:^|\/)cart\.js$/.test(url.pathname)
    );
  }

  function isCartMutation(method, url) {
    return Boolean(
      url &&
      url.origin === window.location.origin &&
      method !== 'GET' &&
      /(?:^|\/)cart\/(?:add|change|update|clear)(?:\.js)?$/.test(url.pathname)
    );
  }

  function invalidateCartCache() {
    cache.generation += 1;
    cache.inFlight = null;
  }

  function responseFromSnapshot(snapshot) {
    if (snapshot.response) return snapshot.response.clone();

    const response = new Response(snapshot.body, {
      status: snapshot.status,
      statusText: snapshot.statusText,
      headers: snapshot.headers
    });

    try {
      Object.defineProperty(response, 'url', { value: snapshot.url });
    } catch (error) {
      // The response remains standards-compatible when a browser keeps url read-only.
    }

    return response;
  }

  function notifyCartResponse(body) {
    try {
      const cart = JSON.parse(body);
      window.dispatchEvent(new CustomEvent('wnp:cart-response', { detail: cart }));
    } catch (error) {
      // Some cart mutations can return section HTML instead of a cart object.
    }
  }

  /** InsCoder 2026-09-18 B + Refresh a stale cart badge when the page is restored from BFCache **/
  let headerCartRefreshPromise = null;
  let pageHasBeenShown = false;

  function refreshHeaderCartIcon() {
    if (headerCartRefreshPromise) return headerCartRefreshPromise;

    const generation = cache.generation;
    headerCartRefreshPromise = (async () => {
      const cartLink = document.getElementById('cart-icon-bubble');
      if (!cartLink) return;

      const sectionUrl = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
      sectionUrl.searchParams.set('sections', 'cart-icon-bubble');

      const response = await nativeFetch(sectionUrl.href, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Cart badge refresh failed with status ${response.status}`);

      const sections = await response.json();
      const sectionHtml = sections['cart-icon-bubble'];
      if (!sectionHtml) return;

      const section = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .querySelector('.shopify-section');
      if (!section) return;
      if (generation !== cache.generation) return;

      cartLink.innerHTML = section.innerHTML;
    })().finally(() => {
      headerCartRefreshPromise = null;
    });

    return headerCartRefreshPromise;
  }

  window.addEventListener('pageshow', () => {
    if (!pageHasBeenShown) {
      pageHasBeenShown = true;
      return;
    }

    invalidateCartCache();
    refreshHeaderCartIcon().catch((error) => {
      console.warn('Unable to refresh the cart badge after restoring the page.', error);
    });
  });
  /** InsCoder 2026-09-18 E + Refresh a stale cart badge when the page is restored from BFCache **/

  function registerInFlight(promise, generation) {
    const entry = { generation, promise: null };
    entry.promise = promise.finally(() => {
      if (cache.inFlight === entry) cache.inFlight = null;
    });
    entry.promise.catch(() => {});
    cache.inFlight = entry;
    return entry;
  }

  window.fetch = async function (...args) {
    const input = args[0];
    const options = args[1] || {};
    const method = requestMethod(input, options);
    const url = toUrl(input);

    if (isCartMutation(method, url)) {
      invalidateCartCache();
      const response = await nativeFetch(...args);
      response.clone().text().then(notifyCartResponse).catch(() => {});
      return response;
    }

    if (!isCartRead(method, url)) return nativeFetch(...args);

    if (cache.inFlight && cache.inFlight.generation === cache.generation) {
      const entry = cache.inFlight;
      const snapshot = await entry.promise;
      if (entry.generation === cache.generation) notifyCartResponse(snapshot.body);
      return responseFromSnapshot(snapshot);
    }

    const generation = cache.generation;
    const request = nativeFetch(...args).then(async (response) => {
      const body = await response.clone().text();
      const snapshot = {
        body,
        headers: Array.from(response.headers.entries()),
        response: response.clone(),
        status: response.status,
        statusText: response.statusText,
        url: response.url || url.href
      };

      return snapshot;
    });
    const entry = registerInFlight(request, generation);
    const snapshot = await entry.promise;
    if (generation === cache.generation) notifyCartResponse(snapshot.body);
    return responseFromSnapshot(snapshot);
  };

  function parseResponseHeaders(rawHeaders) {
    return String(rawHeaders || '')
      .trim()
      .split(/[\r\n]+/)
      .filter(Boolean)
      .map((line) => {
        const separator = line.indexOf(':');
        return separator === -1
          ? [line.trim(), '']
          : [line.slice(0, separator).trim(), line.slice(separator + 1).trim()];
      });
  }

  class CachedCartXMLHttpRequest extends NativeXMLHttpRequest {
    constructor() {
      super();
      this.__wnpRequest = null;
      this.__wnpSyntheticSnapshot = null;
      this.__wnpSyntheticAborted = false;
      this.__wnpSyntheticPending = false;
      this.__wnpRealCartRead = null;
      this.__wnpLifecycle = 0;

      this.addEventListener('load', () => {
        if (this.__wnpRealCartRead) this.__wnpCompleteRealCartRead();

        if (this.__wnpRequest && isCartMutation(this.__wnpRequest.method, this.__wnpRequest.url)) {
          notifyCartResponse(this.__wnpResponseBody());
        }
      });

      ['error', 'abort', 'timeout'].forEach((eventName) => {
        this.addEventListener(eventName, () => {
          if (!this.__wnpRealCartRead) return;
          this.__wnpRealCartRead.reject(new Error(`Cart XMLHttpRequest ${eventName}`));
          this.__wnpRealCartRead = null;
        });
      });
    }

    open(method, url, async = true, username, password) {
      if (this.__wnpRealCartRead) {
        this.__wnpRealCartRead.reject(new Error('Cart XMLHttpRequest reopened'));
        this.__wnpRealCartRead = null;
      }

      this.__wnpLifecycle += 1;
      this.__wnpSyntheticSnapshot = null;
      this.__wnpSyntheticAborted = false;
      this.__wnpSyntheticPending = false;
      this.__wnpRequest = {
        async: async !== false,
        method: String(method || 'GET').toUpperCase(),
        url: toUrl(url)
      };
      return super.open(...arguments);
    }

    send(body = null) {
      const request = this.__wnpRequest;
      if (!request) return super.send(body);

      if (isCartMutation(request.method, request.url)) {
        invalidateCartCache();
        return super.send(body);
      }

      if (!isCartRead(request.method, request.url) || request.async === false) {
        return super.send(body);
      }

      if (cache.inFlight && cache.inFlight.generation === cache.generation) {
        const entry = cache.inFlight;
        const lifecycle = this.__wnpLifecycle;
        this.__wnpSyntheticPending = true;
        entry.promise
          .then((snapshot) => {
            if (lifecycle !== this.__wnpLifecycle) return;
            this.__wnpServeSnapshot(snapshot, entry.generation, lifecycle);
          })
          .catch(() => {
            if (lifecycle !== this.__wnpLifecycle) return;
            this.__wnpSyntheticPending = false;
            super.send(body);
          });
        return;
      }

      const generation = cache.generation;
      let resolveRequest;
      let rejectRequest;
      const requestPromise = new Promise((resolve, reject) => {
        resolveRequest = resolve;
        rejectRequest = reject;
      });
      const entry = registerInFlight(requestPromise, generation);
      this.__wnpRealCartRead = {
        entry,
        generation,
        reject: rejectRequest,
        resolve: resolveRequest
      };

      try {
        return super.send(body);
      } catch (error) {
        this.__wnpRealCartRead = null;
        rejectRequest(error);
        throw error;
      }
    }

    __wnpResponseBody() {
      if (this.responseType === 'json') return JSON.stringify(super.response);
      if (this.responseType && this.responseType !== 'text') return '';
      return super.responseText || '';
    }

    __wnpCompleteRealCartRead() {
      const read = this.__wnpRealCartRead;
      this.__wnpRealCartRead = null;

      const snapshot = {
        body: this.__wnpResponseBody(),
        headers: parseResponseHeaders(super.getAllResponseHeaders()),
        response: null,
        status: super.status,
        statusText: super.statusText,
        url: super.responseURL || this.__wnpRequest.url.href
      };

      if (read.generation === cache.generation) notifyCartResponse(snapshot.body);
      read.resolve(snapshot);
    }

    __wnpServeSnapshot(snapshot, generation, lifecycle) {
      if (lifecycle !== this.__wnpLifecycle) return;

      this.__wnpSyntheticSnapshot = snapshot;
      this.__wnpSyntheticAborted = false;
      this.__wnpSyntheticPending = true;

      queueMicrotask(() => {
        if (lifecycle !== this.__wnpLifecycle || this.__wnpSyntheticAborted) return;
        if (generation === cache.generation) notifyCartResponse(snapshot.body);
        this.dispatchEvent(new Event('readystatechange'));
        if (lifecycle !== this.__wnpLifecycle || this.__wnpSyntheticAborted) return;
        this.dispatchEvent(new ProgressEvent('load', { loaded: snapshot.body.length, total: snapshot.body.length }));
        if (lifecycle !== this.__wnpLifecycle || this.__wnpSyntheticAborted) return;
        this.__wnpSyntheticPending = false;
        this.dispatchEvent(new ProgressEvent('loadend', { loaded: snapshot.body.length, total: snapshot.body.length }));
      });
    }

    abort() {
      const syntheticPending = this.__wnpSyntheticPending;
      this.__wnpLifecycle += 1;
      this.__wnpSyntheticAborted = true;
      this.__wnpSyntheticPending = false;
      this.__wnpSyntheticSnapshot = null;
      super.abort();

      if (syntheticPending) {
        this.dispatchEvent(new Event('abort'));
        this.dispatchEvent(new Event('loadend'));
      }
    }

    get readyState() {
      return this.__wnpSyntheticSnapshot ? 4 : super.readyState;
    }

    get status() {
      return this.__wnpSyntheticSnapshot ? this.__wnpSyntheticSnapshot.status : super.status;
    }

    get statusText() {
      return this.__wnpSyntheticSnapshot ? this.__wnpSyntheticSnapshot.statusText : super.statusText;
    }

    get responseURL() {
      return this.__wnpSyntheticSnapshot ? this.__wnpSyntheticSnapshot.url : super.responseURL;
    }

    get responseText() {
      if (!this.__wnpSyntheticSnapshot) return super.responseText;
      if (this.responseType && this.responseType !== 'text') {
        throw new DOMException('The value is only accessible if responseType is empty or text.', 'InvalidStateError');
      }
      return this.__wnpSyntheticSnapshot.body;
    }

    get response() {
      if (!this.__wnpSyntheticSnapshot) return super.response;
      if (this.responseType === 'json') {
        try {
          return JSON.parse(this.__wnpSyntheticSnapshot.body);
        } catch (error) {
          return null;
        }
      }
      return this.__wnpSyntheticSnapshot.body;
    }

    getAllResponseHeaders() {
      if (!this.__wnpSyntheticSnapshot) return super.getAllResponseHeaders();
      return this.__wnpSyntheticSnapshot.headers.map(([name, value]) => `${name}: ${value}`).join('\r\n');
    }

    getResponseHeader(name) {
      if (!this.__wnpSyntheticSnapshot) return super.getResponseHeader(name);
      const target = String(name).toLowerCase();
      const header = this.__wnpSyntheticSnapshot.headers.find(([headerName]) => headerName.toLowerCase() === target);
      return header ? header[1] : null;
    }
  }

  window.XMLHttpRequest = CachedCartXMLHttpRequest;
})();
/** InsCoder 2026-09-14 E + Deduplicate in-flight cart reads across fetch and XMLHttpRequest **/
